import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Menu } from 'primereact/menu';
import { Button } from 'primereact/button';
import CBMediaQuery from '../../shared/CBMediaQuery';
import { formatDate, formatDateTime, getStyle, SERVICE_CHAIN, SERVICE_WORKSHOP, styleMerge, canResolveApprovalMessage } from '../../../utils/utils';
import OptionsButton from '../../GarageView/GarageViewComponents/OptionsComponents/OptionsButton';
import CBPromptModal from '../../shared/CBPromptModal';
import { withRouter } from 'react-router';
import CBCheckbox from '../../shared/form_components/CBCheckbox';
import CBLabelAndCheckbox from '../../shared/CBLabelAndCheckbox';
import VehicleListTd from '../../OrganizationGarageView/OrganizationGarageComponents/VehicleListTd';
import CBMobileCard from '../../shared/CBMobileCard';
import AddMessageModal from '../../VehicleDetailsView/VehicleDetailsComponents/AddMessageModal';
import AddMessageResponseModal from '../../VehicleDetailsView/VehicleDetailsComponents/AddMessageResponseModal';
import ChainMessageDetailModal from './ChainMessageDetailModal';
import { approveMessage as _approveMessage, forwardMessage as _forwardMessage } from '../../../actions/messageActions';

class MessageListItem extends React.Component {
  state = {
    newMessageModalIsOpen: false,
    deleteModalIsOpen: false,
    responseModalIsOpen: false,
    // 'approved' / 'rejected' preselects the decision in the reply dialog.
    responsePresetDecision: '',
    chainDetailModalIsOpen: false,
  }

  constructor(props) {
    super(props);
    this.settingsMenu = React.createRef();
  }

  componentDidMount() {
    // Opened from an email deep link (?message=<id>): show this message right away.
    const { message, autoOpenMessageId, onAutoOpened } = this.props;
    if (autoOpenMessageId && message && message.id === autoOpenMessageId) {
      this.navigateToMessage();
      if (onAutoOpened) onAutoOpened();
    }
  }

  openNewMessageModal = () => {
    this.setState({
      newMessageModalIsOpen: true,
    });
  };

  closeNewMessageModal = () => {
    this.setState({
      newMessageModalIsOpen: false,
    });
  };

  closeDeletePromptModal = () => {
    this.setState({
      deleteModalIsOpen: false,
    });
  };

  openResponseModal = (presetDecision) => {
    this.setState({
      responseModalIsOpen: true,
      // Cell clicks pass the event here; only a string is a decision.
      responsePresetDecision: typeof presetDecision === 'string' ? presetDecision : '',
    });
  };

  closeResponseModal = () => {
    this.setState({
      responseModalIsOpen: false,
      responsePresetDecision: '',
    });
  };

  openChainDetailModal = () => {
    // Retrieve through the portal endpoint so the message is marked read for
    // this user (the "Luettu" column) and refreshed in the list.
    const { openChainDetail, message } = this.props;
    if (openChainDetail) openChainDetail(message);
    this.setState({
      chainDetailModalIsOpen: true,
    });
  };

  closeChainDetailModal = () => {
    this.setState({
      chainDetailModalIsOpen: false,
    });
  };

  deleteMessage = () => {
    const { message, deleteMessage, service, selectedWorkshopId, selectedChainId } = this.props;
    const wsId = service === SERVICE_WORKSHOP && selectedWorkshopId;
    const chainId = service === SERVICE_CHAIN && selectedChainId;
    deleteMessage(message.id, wsId, chainId);
  };

  checkBoxChanged = () => {
    const {
      selected,
      select,
      unselect,
      message,
    } = this.props;

    if (selected) {
      unselect(message.id);
    } else {
      select(message.id);
    }
  };

  isChainContextMessage = () => {
    const { message } = this.props;
    return message.message_type === 'chain_message'
      || message.message_type === 'approval_request'
      || message.message_type === 'customer_approval'
      || message.message_type === 'availability_inquiry';
  };

  getMessageOptions = (message) => {
    const { garage, t, service } = this.props;
    const isChainContext = this.isChainContextMessage();
    const options = [];

    if (isChainContext) {
      options.push({
        label: t('openMessage'),
        icon: 'pi pi-info',
        command: this.openChainDetailModal,
      });
    } else if (message.vehicle) {
      options.push({
        label: t('openMessage'),
        icon: 'pi pi-info',
        url: `/vehicle/${message.vehicle}/messages/${message.id}`,
      });
    }

    options.push({
      label: t('replyButton'),
      icon: 'pi pi-pencil',
      command: () => {
        this.openResponseModal();
      },
    });

    // approval_request (workshop -> chain) is resolved by the chain; customer_approval
    // (workshop -> fleet customer) is resolved by the organization in the fleet portal and
    // is read-only for the chain. Rejecting goes through the reply dialog so the
    // reason is typed in a proper field.
    if (canResolveApprovalMessage(message, service)) {
      options.push({
        label: t('approveButton'),
        icon: 'pi pi-check',
        command: this.handleApprove,
      });
      options.push({
        label: t('rejectButton'),
        icon: 'pi pi-times',
        command: () => {
          this.openResponseModal('rejected');
        },
      });
    }

    if (message.can_edit) {
      options.push({
        label: t('delete', { ns: 'common' }),
        icon: 'pi pi-trash',
        command: () => {
          this.setState({
            deleteModalIsOpen: true,
          });
        },
      });
    }

    return options;
  };

  navigateToMessage = (messageId) => {
    const { history, message } = this.props;
    if (this.isChainContextMessage()) {
      this.openChainDetailModal();
      return;
    }
    history.push(`/vehicle/${message.vehicle}/messages/${message.id}`);
  };

  getContextDisplay = () => {
    const { message } = this.props;
    if (this.isChainContextMessage()) {
      return '';
    }
    return message.vehicle_registration_number || '';
  };

  getMessageTypeBadge = () => {
    const { message, t, hasChainConnection } = this.props;
    if (!hasChainConnection) return null;

    const badgeBase = {
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 10,
      fontFamily: 'TitilliumWeb-Bold',
      display: 'inline-block',
      marginRight: 4,
    };

    if (message.message_type === 'chain_message') {
      return <span style={{ ...badgeBase, backgroundColor: '#0077b6', color: '#fff' }}>{t('typeChainMessage')}</span>;
    }
    if (message.message_type === 'approval_request') {
      return <span style={{ ...badgeBase, backgroundColor: '#e67e22', color: '#fff' }}>{t('typeApprovalRequest')}</span>;
    }
    if (message.message_type === 'customer_approval') {
      return <span style={{ ...badgeBase, backgroundColor: '#d35400', color: '#fff' }}>{t('typeCustomerApproval')}</span>;
    }
    if (message.message_type === 'availability_inquiry') {
      return <span style={{ ...badgeBase, backgroundColor: '#2e86ab', color: '#fff' }}>{t('typeAvailabilityInquiry')}</span>;
    }
    return <span style={{ ...badgeBase, backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }}>{t('typeVehicleMessage')}</span>;
  };

  getApprovalStatusBadge = () => {
    const { message, t } = this.props;
    const isApprovalType = message.message_type === 'approval_request'
      || message.message_type === 'customer_approval';
    if (!isApprovalType || !message.approval_status) {
      return null;
    }
    const badgeColors = {
      pending: { backgroundColor: '#e67e22', color: '#fff' },
      approved: { backgroundColor: '#27ae60', color: '#fff' },
      rejected: { backgroundColor: '#e74c3c', color: '#fff' },
    };
    const labels = {
      pending: t('approvalPending'),
      approved: t('approvalApproved'),
      rejected: t('approvalRejected'),
    };
    const style = {
      ...badgeColors[message.approval_status],
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontFamily: 'TitilliumWeb-Bold',
      display: 'inline-block',
    };
    return <span style={style}>{labels[message.approval_status] || message.approval_status}</span>;
  };

  getSubjectSecondary = () => {
    const typeBadge = this.getMessageTypeBadge();
    const statusBadge = this.getApprovalStatusBadge();
    if (!typeBadge && !statusBadge) return null;
    return (
      <span>
        {typeBadge}
        {statusBadge}
      </span>
    );
  };

  getUserDisplaySecondary = () => {
    const { message } = this.props;
    if (this.isChainContextMessage() && message.sender_workshop_name) {
      return message.sender_workshop_name;
    }
    return null;
  };

  handleApprove = () => {
    const { message, approveMessage, selectedChainId } = this.props;
    const chainId = message.chain_id || selectedChainId;
    if (approveMessage && chainId) {
      approveMessage(message.id, chainId);
    }
  };

  handleForward = (messageId, data) => {
    const { forwardMessage, selectedChainId, message } = this.props;
    const chainId = message.chain_id || selectedChainId;
    if (forwardMessage && chainId) {
      return forwardMessage(messageId, chainId, data);
    }
    return Promise.resolve();
  };

  getCardTitle = () => {
    const { message } = this.props;
    return this.getContextDisplay() || message.subject || '-';
  };

  getCardBadges = () => {
    var badges = [];
    var typeBadge = this.getMessageTypeBadge();
    var statusBadge = this.getApprovalStatusBadge();
    if (typeBadge) { badges.push(typeBadge); }
    if (statusBadge) { badges.push(statusBadge); }
    return badges;
  };

  getCardFaceFields = () => {
    const { message, t } = this.props;
    var fields = [];
    // Only show subject as a field when it isn't already the card title (chain
    // messages without a vehicle context use the subject as the title).
    if (this.getContextDisplay()) {
      fields.push({ label: t('headingMessageTitle'), value: message.subject || '-' });
    }
    fields.push({ label: t('headingMessageSender'), value: [message.user_display, this.getUserDisplaySecondary()].filter(Boolean).join(' · ') || '-' });
    fields.push({ label: t('headingMessageSentAt'), value: formatDateTime(message.created_at) });
    return fields;
  };

  getCardAllFields = () => {
    const { message, t, useVehicleIdentifier } = this.props;
    var fields = [];
    fields.push({ label: t('headingRegistrationNumber'), value: message.vehicle_registration_number || '-' });
    if (useVehicleIdentifier && message.vehicle_identifier) {
      fields.push({ label: t('headingVehicleIdentifier'), value: message.vehicle_identifier });
    }
    fields.push({ label: t('headingManufacturer'), value: message.vehicle_model || '-' });
    fields.push({ label: t('headingMessageTitle'), value: message.subject || '-' });
    fields.push({ label: t('headingMessageSender'), value: [message.user_display, this.getUserDisplaySecondary()].filter(Boolean).join(' · ') || '-' });
    fields.push({ label: t('headingMessageSentAt'), value: formatDateTime(message.created_at) });
    fields.push({ label: t('headingReply'), value: (message.responses && message.responses.length) ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' }) });
    fields.push({ label: t('headingRead'), value: message.is_read ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' }) });
    return fields;
  };

  renderMobileCard = () => {
    const { message } = this.props;
    return (
      <CBMobileCard
        title={this.getCardTitle()}
        dialogTitle={this.getCardTitle()}
        subtitle={message.vehicle_model}
        badges={this.getCardBadges()}
        fields={this.getCardFaceFields()}
        allFields={this.getCardAllFields()}
        options={this.getMessageOptions(message)}
        onClick={() => this.navigateToMessage(message.id)}
      />
    );
  };

  render() {
    const {
      message,
      t,
      selectedFields,
      garage,
      removeMessageFromGarage,
      selected,
      vehicles,
      useVehicleIdentifier,
      service,
    } = this.props;
    const { deleteModalIsOpen, newMessageModalIsOpen, responseModalIsOpen, chainDetailModalIsOpen } = this.state;

    return (
      <CBMediaQuery>
        {screenSize => (
          <React.Fragment>
            {screenSize === 'small' ? (
              this.renderMobileCard()
            ) : (
            <tr style={getStyle(screenSize, styles, 'messageRow')}>
              {
                /*
                <td style={getStyle(screenSize, styles, 'tableRegNumTd')}>
                  <CBLabelAndCheckbox
                    additionalStyles={{ justifyContent: 'center' }}
                    checked={selected}
                    onChange={this.checkBoxChanged}
                  />
                </td>
                */
              }

              {
                selectedFields.includes('vehicle_identifier') && useVehicleIdentifier && (
                  <VehicleListTd
                    onClick={this.navigateToTask}
                    textPrimary={message.vehicle_identifier}
                    additionalStylesPrimaryText={{ fontSize: 24 }}
                  />
                )
              }

              <VehicleListTd
                onClick={this.navigateToMessage}
                textPrimary={this.getContextDisplay()}
                additionalStylesPrimaryText={{ fontSize: 24 }}
              />

              {
                selectedFields.includes('vehicle_model') && (
                  <VehicleListTd
                    onClick={this.navigateToMessage}
                    textPrimary={message.vehicle_model || ''}
                  />
                )
              }
              {
                selectedFields.includes('subject') && (
                  <VehicleListTd
                    onClick={this.navigateToMessage}
                    textPrimary={message.subject}
                    textSecondary={this.getSubjectSecondary()}
                  />
                )
              }
              {
                selectedFields.includes('user_display') && (
                  <VehicleListTd
                    onClick={this.navigateToMessage}
                    textPrimary={message.user_display}
                    textSecondary={this.getUserDisplaySecondary()}
                  />
                )
              }
              {
                selectedFields.includes('created_at') && (
                  <VehicleListTd
                    onClick={this.navigateToMessage}
                    textPrimary={formatDateTime(message.created_at)}
                  />
                )
              }
              {
                selectedFields.includes('task') && (
                  <VehicleListTd
                    additionalStyles={getStyle(screenSize, styles, 'alignCenter')}
                  />
                )
              }
              {
                selectedFields.includes('has_response') && (
                  <VehicleListTd
                    onClick={this.openResponseModal}
                    icon={message.responses.length ? '/img/icon_reply.svg' : '/img/icon_no_reply.svg'}
                    additionalStyles={getStyle(screenSize, styles, 'alignCenter')}
                  />
                )
              }
              {
                selectedFields.includes('is_read') && (
                  <VehicleListTd
                    onClick={this.navigateToMessage}
                    icon={message.is_read ? '/img/icon_is_read.svg' : undefined}
                    additionalStyles={getStyle(screenSize, styles, 'alignCenter')}
                  />
                )
              }
              {
                selectedFields.includes('new_message') && (
                  <VehicleListTd
                    onClick={this.openNewMessageModal}
                    icon='/img/icon_new_message.svg'
                    additionalStyles={getStyle(screenSize, styles, 'alignCenter')}
                  />
                )
              }
              <td style={getStyle(screenSize, styles, 'tableOptionsTd')}>
                <Menu model={this.getMessageOptions(message)} popup ref={this.settingsMenu} />
                <div
                  style={getStyle(screenSize, styles, 'container')}
                  onClick={(e) => {this.settingsMenu.current.toggle(e)}}
                >
                  <img
                    alt='Options'
                    className='opacity-and-border-on-hover'
                    style={getStyle(screenSize, styles, 'optionsIcon')}
                    src='/img/icon_options.svg'
                  />
                </div>
              </td>
            </tr>
            )}
            <CBPromptModal
              modalIsOpen={deleteModalIsOpen}
              closeModal={this.closeDeleteModal}
              afterOpenModal={this.afterOpenDeleteModal}
              title={t('deleteMessagePromptTitle')}
              text={t('deleteMessagePromptText')}
              buttonText={t('deleteMessagePromptButton')}
              performAction={this.deleteMessage}
            />
            {newMessageModalIsOpen && (
              <AddMessageModal
                modalIsOpen={newMessageModalIsOpen}
                closeModal={this.closeNewMessageModal}
                vehicle={vehicles.find((veh) => veh.id == message.vehicle)}
                isListRequest
              />
            )}
            {responseModalIsOpen && (
              <AddMessageResponseModal
                modalIsOpen={responseModalIsOpen}
                closeModal={this.closeResponseModal}
                message={message}
                presetDecision={this.state.responsePresetDecision}
                isListRequest
              />
            )}
            {chainDetailModalIsOpen && (
              <ChainMessageDetailModal
                message={message}
                modalIsOpen={chainDetailModalIsOpen}
                closeModal={this.closeChainDetailModal}
                service={service}
                approveMessage={this.handleApprove}
                forwardMessage={this.handleForward}
                chainMemberships={this.props.chainMemberships}
                chainCareServices={this.props.chainCareServices}
              />
            )}

          </React.Fragment>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    infoIcon: {
      height: 32,
      width: 32,
    },
    alignCenter: {
      textAlign: 'center',
    },
    messageRow: {
      height: '60px',
      width: '100%',
      marginBottom: 2,

    },
    imgPlaceholder: {
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
      width: 100,
      height: 60,
    },
    messageImageColumn: {
      width: 100,
      padding: 8,
      cursor: 'pointer',
      borderBottom: '1px solid rgba(255, 255, 255, .3)',
    },
    tableValueTd: {
      fontFamily: 'TitilliumWeb-Light',
      fontSize: 18,
      color: '#FFFFFF',
      padding: 8,
      cursor: 'pointer',
      borderBottom: '1px solid rgba(255, 255, 255, .3)',
    },
    optionsIcon: {
      cursor: 'pointer',
    },
    tableRegNumTd: {
      fontFamily: 'TitilliumWeb-Bold',
      fontSize: 18,
      color: '#FFFFFF',
      //position: 'sticky',
      left: 0,
      padding: '8px 16px 8px 16px',
      borderBottom: '1px solid rgba(255, 255, 255, .3)',
    },
    tableOptionsTd: {
      //position: 'sticky',
      right: 0,
      padding: '0 16px 0 16px',
      zIndex: 0,
    },
  },
  medium: {
  },
  small: {
  },
};

function mapStateToProps(state) {
  return {
    selectedChainId: state.chain.selectedChainId,
    chainMemberships: state.chain.chainMemberships,
    chainCareServices: state.chain.chainCareServices,
  };
}

export default connect(
  mapStateToProps,
  {
    approveMessage: _approveMessage,
    forwardMessage: _forwardMessage,
  },
)(translate('MessageListView')(withRouter(MessageListItem)));
